import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Loader2, Plus, Trash2, Download, Mail, Check, Copy, XCircle, Search, Link2, MessageSquare,
} from "lucide-react";
import { API, fmtDate } from "./shared";

const inputCls =
  "bg-white border border-[#BCB3A6]/50 rounded-2xl px-3 py-2 text-sm text-[#3F5043] focus:outline-none focus:ring-2 focus:ring-[#899480]";

const INVOICE_TYPES = [
  ["fertility_consulting", "Fertility Consulting"],
  ["translation_services", "Translation Services"],
  ["patient_support", "Patient Support Services"],
  ["care_coordination", "Care Coordination"],
  ["package_services", "Package Services"],
  ["custom", "Custom Services"],
];

const STATUS_CLS = {
  draft: "bg-[#EDEDEE] text-[#3F5043]",
  sent: "bg-[#D4AF37]/20 text-[#875F45]",
  unpaid: "bg-red-100 text-red-700",
  paid: "bg-[#899480]/15 text-[#3F5043]",
  void: "bg-[#EDEDEE] text-[#899480] line-through",
};

const EMPTY = {
  client_email: "", client_name: "", invoice_date: "", due_date: "",
  items: [{ description: "", service_type: "fertility_consulting", quantity: 1, unit_price: 0 }],
  tax_rate: 0, notes: "",
};

const fmtUsd = (n) => `$${Number(n || 0).toFixed(2)}`;

export function InvoicesTab({ authHeader }) {
  const [invoices, setInvoices] = useState(null);
  const [summary, setSummary] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // null | "new" | invoice id
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    try {
      const [list, sum] = await Promise.all([
        axios.get(`${API}/admin/invoices`, {
          headers: authHeader(),
          params: { status: statusFilter || undefined, q: search || undefined },
        }),
        axios.get(`${API}/admin/invoices-summary`, { headers: authHeader() }),
      ]);
      setInvoices(list.data);
      setSummary(sum.data);
    } catch {}
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (inv) => {
    setForm({
      client_email: inv.client_email, client_name: inv.client_name,
      invoice_date: inv.invoice_date, due_date: inv.due_date || "",
      items: inv.items.map((i) => ({ ...i })),
      tax_rate: inv.tax_rate || 0, notes: inv.notes || "",
    });
    setEditing(inv.id);
  };

  const setItem = (idx, patch) => {
    const items = form.items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    setForm({ ...form, items });
  };

  const totals = (() => {
    const sub = form.items.reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0), 0);
    const tax = sub * (Number(form.tax_rate) || 0) / 100;
    return { sub, tax, total: sub + tax };
  })();

  const save = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setMsg("");
    try {
      const payload = { ...form, quantity: undefined };
      if (editing === "new") {
        await axios.post(`${API}/admin/invoices`, payload, { headers: authHeader() });
        setMsg("Invoice created.");
      } else {
        await axios.patch(`${API}/admin/invoices/${editing}`, payload, { headers: authHeader() });
        setMsg("Invoice updated.");
      }
      setEditing(null);
      await load();
    } catch (e2) {
      setMsg(e2?.response?.data?.detail || "Could not save the invoice.");
    } finally { setBusy(false); }
  };

  const act = async (id, action, body = {}) => {
    try {
      const res = await axios.post(`${API}/admin/invoices/${id}/${action}`, body, { headers: authHeader() });
      if (action === "send" && res.data.email === "failed") {
        setMsg("Invoice saved as sent — but the email could not be delivered (email service is being restored). Use Copy link or PDF to send it manually.");
      } else if (action === "text-link" && res.data.texted) {
        setMsg(`Pay link texted to ${res.data.phone}.`);
      } else {
        setMsg("");
      }
      await load();
    } catch (e2) {
      alert(e2?.response?.data?.detail || "Action failed.");
    }
  };

  const copyPayLink = async (inv) => {
    const link = `${window.location.origin}/pay/${inv.pay_token}`;
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      window.prompt("Copy this link:", link);
    }
    setMsg(`Pay link copied for ${inv.invoice_number} — paste it in a text or WhatsApp to the client.`);
  };

  const downloadPdf = async (inv) => {
    const res = await axios.get(`${API}/admin/invoices/${inv.id}/pdf`, {
      headers: authHeader(), responseType: "blob",
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${inv.invoice_number}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="invoices-tab">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="font-serif text-2xl text-[#3F5043]">Invoices</div>
          <p className="text-sm text-[#3F5043]/70">Create, send, and track client invoices.</p>
        </div>
        <button onClick={openNew} data-testid="invoice-new-btn"
          className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 bg-[#3F5043] text-[#F8F7F3] text-xs uppercase tracking-[0.15em] font-semibold hover:bg-[#875F45] transition-colors">
          <Plus size={13} /> New invoice
        </button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6" data-testid="payments-dashboard">
          {[
            ["Collected · all time", fmtUsd(summary.collected_all_time), "inv-collected", "border-l-[#899480]"],
            ["Revenue this month", fmtUsd(summary.revenue_this_month), "inv-revenue", "border-l-[#3F5043]"],
            ["Outstanding", `${summary.outstanding_count} · ${fmtUsd(summary.outstanding_amount)}`, "inv-outstanding", "border-l-[#D4AF37]"],
            ["Overdue", `${summary.overdue_count} · ${fmtUsd(summary.overdue_amount)}`, "inv-overdue", "border-l-red-400"],
            ["Drafts", summary.draft_count, "inv-drafts", "border-l-[#BCB3A6]"],
          ].map(([l, v, tid, cls]) => (
            <div key={tid} data-testid={tid} className={`bg-white rounded-2xl border border-[#BCB3A6]/40 p-4 border-l-4 ${cls}`}>
              <div className="text-[9px] uppercase tracking-[0.18em] text-[#899480] font-semibold mb-1">{l}</div>
              <div className="font-serif text-xl text-[#3F5043]">{v}</div>
            </div>
          ))}
        </div>
      )}

      {msg && <div className="mb-4 bg-[#D4AF37]/10 border border-[#D4AF37]/40 rounded-2xl p-3 text-sm text-[#3F5043]" data-testid="invoices-msg">{msg}</div>}

      {editing && (
        <form onSubmit={save} data-testid="invoice-form" className="bg-white border border-[#BCB3A6]/40 rounded-3xl p-5 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <input required placeholder="Client name" value={form.client_name} data-testid="inv-client-name"
              onChange={(e) => setForm({ ...form, client_name: e.target.value })} className={inputCls} />
            <input required type="email" placeholder="Client email" value={form.client_email} data-testid="inv-client-email"
              onChange={(e) => setForm({ ...form, client_email: e.target.value })} className={inputCls} />
            <div>
              <label className="block text-[10px] uppercase tracking-[0.15em] text-[#899480] mb-1">Invoice date</label>
              <input type="date" value={form.invoice_date} data-testid="inv-date"
                onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} className={`${inputCls} w-full`} />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.15em] text-[#899480] mb-1">Due date</label>
              <input type="date" value={form.due_date} data-testid="inv-due"
                onChange={(e) => setForm({ ...form, due_date: e.target.value })} className={`${inputCls} w-full`} />
            </div>
          </div>

          <div className="text-[10px] uppercase tracking-[0.2em] text-[#899480] font-semibold mb-2">Line items</div>
          <div className="space-y-2 mb-3">
            {form.items.map((it, idx) => (
              <div key={idx} className="flex flex-wrap gap-2 items-center" data-testid={`inv-item-${idx}`}>
                <input required placeholder="Service description" value={it.description} data-testid={`inv-item-desc-${idx}`}
                  onChange={(e) => setItem(idx, { description: e.target.value })} className={`${inputCls} flex-1 min-w-[180px]`} />
                <select value={it.service_type} data-testid={`inv-item-type-${idx}`}
                  onChange={(e) => setItem(idx, { service_type: e.target.value })} className={inputCls}>
                  {INVOICE_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <input type="number" min="0.5" step="0.5" value={it.quantity} data-testid={`inv-item-qty-${idx}`}
                  onChange={(e) => setItem(idx, { quantity: Number(e.target.value) })} className={`${inputCls} w-20`} title="Qty" />
                <input type="number" min="0" step="0.01" value={it.unit_price} data-testid={`inv-item-price-${idx}`}
                  onChange={(e) => setItem(idx, { unit_price: Number(e.target.value) })} className={`${inputCls} w-24`} title="Unit price" />
                {form.items.length > 1 && (
                  <button type="button" onClick={() => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) })}
                    data-testid={`inv-item-remove-${idx}`} className="p-2 text-[#875F45] hover:bg-[#875F45]/10 rounded-full" aria-label="Remove item">
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
          <button type="button" onClick={() => setForm({ ...form, items: [...form.items, { description: "", service_type: "custom", quantity: 1, unit_price: 0 }] })}
            data-testid="inv-add-item"
            className="text-xs text-[#3F5043] font-semibold inline-flex items-center gap-1 hover:text-[#875F45] mb-4">
            <Plus size={12} /> Add line item
          </button>

          <div className="flex flex-wrap items-end gap-4 mb-4">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.15em] text-[#899480] mb-1">Tax %</label>
              <input type="number" min="0" max="100" step="0.1" value={form.tax_rate} data-testid="inv-tax"
                onChange={(e) => setForm({ ...form, tax_rate: Number(e.target.value) })} className={`${inputCls} w-24`} />
            </div>
            <div className="text-sm text-[#3F5043]" data-testid="inv-totals">
              Subtotal {fmtUsd(totals.sub)} · Tax {fmtUsd(totals.tax)} · <strong>Total {fmtUsd(totals.total)}</strong>
            </div>
          </div>
          <textarea placeholder="Notes (shown on invoice)" value={form.notes} data-testid="inv-notes" rows={2}
            onChange={(e) => setForm({ ...form, notes: e.target.value })} className={`${inputCls} w-full resize-none mb-4`} />

          <div className="flex gap-3">
            <button type="submit" data-testid="inv-save" disabled={busy}
              className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 bg-[#3F5043] text-[#F8F7F3] text-xs uppercase tracking-[0.18em] font-semibold hover:bg-[#875F45] transition-colors disabled:opacity-60">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
              {editing === "new" ? "Create invoice" : "Save changes"}
            </button>
            <button type="button" onClick={() => setEditing(null)} className="text-xs uppercase tracking-[0.15em] text-[#899480]">Cancel</button>
          </div>
        </form>
      )}

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#899480]" />
          <input placeholder="Search number, client…" value={search} data-testid="inv-search"
            onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
            className={`${inputCls} pl-9 w-56`} />
        </div>
        <select value={statusFilter} data-testid="inv-status-filter"
          onChange={(e) => setStatusFilter(e.target.value)} className={inputCls}>
          <option value="">All statuses</option>
          {["draft", "sent", "unpaid", "paid", "void"].map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
        </select>
        <button onClick={load} data-testid="inv-apply"
          className="text-xs uppercase tracking-[0.15em] font-semibold rounded-full px-5 py-2 bg-[#3F5043] text-[#F8F7F3] hover:bg-[#875F45] transition-colors">
          Apply
        </button>
      </div>

      {!invoices ? (
        <div className="text-center py-10"><Loader2 size={18} className="animate-spin inline text-[#899480]" /></div>
      ) : invoices.length === 0 ? (
        <div className="bg-white rounded-2xl border border-[#BCB3A6]/40 p-8 text-center text-sm text-[#899480]" data-testid="invoices-empty">
          No invoices yet — create your first above.
        </div>
      ) : (
        <div className="space-y-2" data-testid="invoices-list">
          {invoices.map((inv) => (
            <div key={inv.id} data-testid={`invoice-row-${inv.id}`}
              className="bg-white border border-[#BCB3A6]/40 rounded-2xl px-4 py-3">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-[#3F5043] flex items-center gap-2">
                    {inv.invoice_number}
                    <span className={`text-[9px] uppercase tracking-[0.12em] font-bold px-2 py-0.5 rounded-full ${STATUS_CLS[inv.status] || STATUS_CLS.draft}`}>
                      {inv.status}
                    </span>
                  </div>
                  <div className="text-xs text-[#899480]">
                    {inv.client_name} · {fmtDate(inv.invoice_date)}{inv.due_date ? ` · due ${fmtDate(inv.due_date)}` : ""} · {fmtUsd(inv.total)}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <button onClick={() => openEdit(inv)} data-testid={`invoice-edit-${inv.id}`}
                    className="text-xs rounded-full px-3 py-1.5 border border-[#899480]/50 text-[#3F5043] hover:bg-[#899480]/10 transition-colors">
                    Edit
                  </button>
                  <button onClick={() => downloadPdf(inv)} data-testid={`invoice-pdf-${inv.id}`}
                    className="inline-flex items-center gap-1 text-xs rounded-full px-3 py-1.5 border border-[#899480]/50 text-[#3F5043] hover:bg-[#899480]/10 transition-colors">
                    <Download size={11} /> PDF
                  </button>
                  <button onClick={() => act(inv.id, "send")} data-testid={`invoice-send-${inv.id}`}
                    className="inline-flex items-center gap-1 text-xs rounded-full px-3 py-1.5 border border-[#D4AF37]/60 text-[#875F45] hover:bg-[#D4AF37]/10 transition-colors">
                    <Mail size={11} /> Send
                  </button>
                  <button onClick={() => copyPayLink(inv)} data-testid={`invoice-copy-link-${inv.id}`}
                    className="inline-flex items-center gap-1 text-xs rounded-full px-3 py-1.5 bg-[#D4AF37]/15 border border-[#D4AF37]/60 text-[#875F45] hover:bg-[#D4AF37]/25 transition-colors">
                    <Link2 size={11} /> Copy pay link
                  </button>
                  <button onClick={() => act(inv.id, "text-link", { origin_url: window.location.origin })} data-testid={`invoice-text-link-${inv.id}`}
                    className="inline-flex items-center gap-1 text-xs rounded-full px-3 py-1.5 border border-[#899480]/50 text-[#3F5043] hover:bg-[#899480]/10 transition-colors">
                    <MessageSquare size={11} /> Text link
                  </button>
                  <button onClick={() => act(inv.id, "duplicate")} data-testid={`invoice-duplicate-${inv.id}`}
                    className="inline-flex items-center gap-1 text-xs rounded-full px-3 py-1.5 border border-[#899480]/50 text-[#3F5043] hover:bg-[#899480]/10 transition-colors">
                    <Copy size={11} /> Duplicate
                  </button>
                  {inv.status !== "paid" ? (
                    <button onClick={() => act(inv.id, "status", { status: "paid", method: "manual" })} data-testid={`invoice-paid-${inv.id}`}
                      className="inline-flex items-center gap-1 text-xs rounded-full px-3 py-1.5 bg-[#3F5043] text-[#F8F7F3] hover:bg-[#899480] transition-colors">
                      <Check size={11} /> Mark paid
                    </button>
                  ) : (
                    <button onClick={() => act(inv.id, "status", { status: "unpaid" })} data-testid={`invoice-unpaid-${inv.id}`}
                      className="inline-flex items-center gap-1 text-xs rounded-full px-3 py-1.5 border border-red-300 text-red-700 hover:bg-red-50 transition-colors">
                      <XCircle size={11} /> Mark unpaid
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
